import {
  AnyAssetApplication,
  AnyAssetSubType,
  Divider,
  FormikFormAsset,
  FormikNewFormValues,
  insertArrayIf,
  NetworkAsset,
  Page,
  PageContent,
  PermissionGuard,
  PlcDcsFCSAsset,
  Stepper,
  useAssetConfig,
  useAssetTranslation,
} from '@mss/mss-component-library';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumbs from '../../Breadcrumbs';
import usePermissionsForType from '../../../asset/util/useAddPermissions';
import { criticalityOptions } from '../../../asset/util/criticalityOptions';
import ImportOnboarding from './ImportOnboarding';
import UploadCSV from './UploadCSV';
import UploadResults from './UploadResults';
import {
  exportAssetTypeSubTypeKey,
  paramsToExportAssetType,
  paramsToSteps,
} from './util';
import { Step } from './types';
import Button from '../../Button';

const skipConnectionTestStatus = { port: true, account: true, query: true };
const connectionTestStatus = { port: null, account: null, query: null };
export const emptyConnectionTest = (skip?: boolean) => ({
  loading: false,
  result: {
    message: '',
    status: skip ? skipConnectionTestStatus : connectionTestStatus,
  },
});

type Props = {
  disableImportSteps?: boolean;
  values?: FormikNewFormValues<any>;
};

export default function Import<AssetType extends AnyAssetApplication>({
  disableImportSteps = false,
  values,
}: Props) {
  const { t } = useAssetTranslation();
  const { assetType } = useParams<any>();
  const { config } = useAssetConfig();
  const exportType = paramsToExportAssetType(assetType);
  const [formValidFail, setformValidFail] = useState(false);
  const preFillStep = { label: t('Settings'), value: 'preFill' };
  const allSteps: Step[] = [
    ...insertArrayIf(!disableImportSteps, [
      { label: t('Upload file'), value: 'upload' },
      { label: t('Analyze results'), value: 'analyze' },
    ]),
    ...(exportType === 'field' ? [preFillStep] : []),
    ...paramsToSteps(exportType),
  ];
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [assetsFromCsv, setAssetsFromCsv] = useState<AssetType[]>([]);
  const [invalidKeys, setInvalidKeys] = useState<string[]>([]);
  const addPermissions = usePermissionsForType(exportType, 'add');
  const [initialOnboardingValues, setInitialOnboardingValues] =
    useState<FormikNewFormValues<AssetType> | null>(values ?? null);

  const resetToFirstStep = () => {
    setCurrentStep(0);
    setAssetsFromCsv([]);
    setInvalidKeys([]);
    setInitialOnboardingValues(null);
  };

  const goToNextStep = () => setCurrentStep(currentValue => currentValue + 1);
  const goToPreviousStep = () =>
    setCurrentStep(currentValue => currentValue - 1);

  const isImportStep = ['upload', 'analyze'].includes(
    allSteps[currentStep].value
  );

  useEffect(() => {
    if (isImportStep) setInitialOnboardingValues(null);
  }, [isImportStep]);

  useEffect(() => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }, [currentStep]);

  return (
    <PermissionGuard any codename={addPermissions} redirect>
      <Page
        breadCrumbs={<Breadcrumbs />}
        main={
          <PageContent
            actions={
              allSteps[currentStep].value === 'connection' ? (
                <div>
                  <p>
                    {t(
                      'If you want to skip all connection tests to speed up the onboarding process, click on the button below. Please keep in mind that every asset will still be onboarded with the given credentials.'
                    )}
                  </p>
                  <Button
                    variant="primary"
                    className="w-full"
                    ghost
                    onClick={() => setCurrentStep(x => x + 1)}
                    disabled={formValidFail}
                  >
                    {t('Skip all connection tests')}
                  </Button>
                  <Divider />
                </div>
              ) : null
            }
            title={
              <Stepper
                currentStep={{ index: currentStep, set: setCurrentStep }}
                steps={allSteps}
              />
            }
          >
            {allSteps[currentStep].value === 'upload' && (
              <UploadCSV<AssetType>
                type={exportType}
                setAssetsFromCsv={setAssetsFromCsv}
                setInvalidKeys={setInvalidKeys}
                goToNextStep={goToNextStep}
              />
            )}
            {allSteps[currentStep].value === 'analyze' && (
              <UploadResults<any>
                invalidKeys={invalidKeys}
                assetsFromCsv={assetsFromCsv}
                type={exportType}
                setAssetsForOnboarding={(
                  assets: (AssetType & {
                    tempId: number;
                  })[]
                ) => {
                  setInitialOnboardingValues({
                    assets: assets.map((asset): FormikFormAsset<any> => {
                      const subType =
                        asset[exportAssetTypeSubTypeKey(exportType).key];
                      const matchingConfig =
                        config.assets[subType as AnyAssetSubType];
                      const isFCN = subType === 'fcn';
                      const isPLCDCS = exportType === 'dcs_plc';
                      const isNetwork = exportType === 'network';

                      const { station_number, domain_number, ..._asset } =
                        (matchingConfig?.form.fetchDataParser?.(asset) ??
                          asset) as PlcDcsFCSAsset;
                      const networkAsset =
                        (matchingConfig?.form.fetchDataParser?.(asset) ??
                          asset) as NetworkAsset;

                      return {
                        values: {
                          ..._asset,
                          custom_field_values: {} as Record<string, any>,
                          name: _asset.name,
                          priority: criticalityOptions(t)
                            .map(c => c.value)
                            .includes(asset.priority?.toLowerCase())
                            ? _asset.priority.toLowerCase()
                            : '',
                          ...(!isFCN && isPLCDCS
                            ? {
                                station_number,
                                domain_number,
                              }
                            : {}),
                          ...(isNetwork
                            ? {
                                brand: isNaN(parseInt(networkAsset.brand))
                                  ? ''
                                  : parseInt(networkAsset.brand),
                                series: isNaN(parseInt(networkAsset.series))
                                  ? ''
                                  : parseInt(networkAsset.series),
                              }
                            : {}),
                        },
                        test_direct: emptyConnectionTest(),
                        test_ssh: emptyConnectionTest(),
                        test_telnet: emptyConnectionTest(),
                        test_snmp: emptyConnectionTest(),
                      };
                    }),
                  });
                  goToNextStep();
                }}
                goToPreviousStep={resetToFirstStep}
              />
            )}
            {!isImportStep && initialOnboardingValues && (
              <ImportOnboarding<any>
                currentStep={currentStep}
                type={exportType}
                initialValues={initialOnboardingValues}
                restart={disableImportSteps ? undefined : resetToFirstStep}
                goToPreviousStep={goToPreviousStep}
                goToNextStep={goToNextStep}
                enableSubmit={currentStep === allSteps.length - 1}
                currentStepValue={allSteps[currentStep].value}
                onValidationFailed={setformValidFail}
              />
            )}
          </PageContent>
        }
      />
    </PermissionGuard>
  );
}
